import { useIsTouchDevice } from "@h/useResponsive";
import ResponsiveSheet from "@ui/responsive-sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/tooltip";
import { Info } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

const defaultIcon = (
  <Info
    size={14}
    className="text-muted-foreground hover:text-primary transition-colors"
  />
);

export default function InfoTooltip({
  text,
  icon = defaultIcon,
}: {
  text: string;
  icon?: ReactNode;
}) {
  const isTouch = useIsTouchDevice();
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  // 1. LÓGICA TÁCTIL
  if (isTouch) {
    return (
      <>
        <button
          type="button"
          aria-label={t("more_info", "Más información")}
          onClick={(e) => {
            // Solucionar el error de aria-hidden:
            // Quitamos el foco del botón para que no intente mantener el foco
            // dentro de un contenedor que será marcado como aria-hidden.
            (e.currentTarget as HTMLElement).blur();
            setSheetOpen(true);
          }}
          className="flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
        >
          {icon}
        </button>

        <ResponsiveSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t("information")}
          variant="glass"
        >
          <div className="p-2 text-sm text-foreground">{text}</div>
        </ResponsiveSheet>
      </>
    );
  }

  // 2. LÓGICA DESKTOP
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* ponytail: tabIndex=-1 keeps this decorative hint out of the tab
              order so Radix Dialog autofocus doesn't land here and pop the
              tooltip open on mount. Hover still works; add real focus handling
              if these ever carry non-redundant info. */}
          <button
            type="button"
            tabIndex={-1}
            aria-label={t("more_info", "Más información")}
            className="flex items-center justify-center p-0 border-none bg-transparent cursor-help"
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={5} className="max-w-xs text-xs font-normal">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
